import React from "react";
import styled from "styled-components";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { Copy, H4 } from "../Typography";
import prefersReducedMotion from "../../prefersReducedMotion";

const Wrapper = styled.div`
  transform: translate(
    ${({ $inView, $slidesIn }) =>
      $slidesIn ? ($inView ? "0, 0" : "-80vw, 0") : "0, 8px"}
  );
  transition: transform 0.2s linear;
  margin-bottom: ${({ $slidesIn }) => $slidesIn && "30px"};

  @media (prefers-reduced-motion) {
    transform: translate(0, 8px);
    transition: none;
  }
`;

const NumberWrapper = styled.div`
  padding-bottom: 30px;
  margin-bottom: 24px;
  border-bottom: 1px solid
    ${({ theme, $colour }) =>
      $colour !== "white" ? theme.colours.lightGrey : theme.colours[$colour]};

  @media (${({ theme }) => theme.mq.m}) {
    display: flex;
    align-items: flex-end;
    flex-direction: ${({ $slidesIn }) => $slidesIn && "row-reverse"};
  }
`;

const numberStyles = `
  font-family: "supply-light", monospace;
  font-size: 105px;
  line-height: 100px;
  letter-spacing: -0.07em;
  margin-right: 30px;
  margin-bottom: 5px;
  margin-left: -0.07em;
  display: block;
`;

const numberMediumStyles = `
  font-size: 135px;
  line-height: 130px;
  margin-bottom: 0;
  // adjustment for diacritics and descenders
  transform: translateY(8px);
`;

const CountUpNumber = styled(CountUp)`
  color: ${({ theme, $colour = "blue" }) => theme.colours[$colour]};
  ${numberStyles};

  @media (${({ theme }) => theme.mq.m}) {
    ${numberMediumStyles};
    margin-left: ${({ $slidesIn }) => ($slidesIn ? "20px" : "-0.07em")};
  }
`;
const Number = styled.span`
  color: ${({ theme, $colour = "blue" }) => theme.colours[$colour]};
  ${numberStyles};

  @media (${({ theme }) => theme.mq.m}) {
    ${numberMediumStyles};
    margin-left: ${({ $slidesIn }) => ($slidesIn ? "20px" : "-0.07em")};
  }
`;

const Sources = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;

  @media (${({ theme }) => theme.mq.m}) {
    flex-direction: ${({ $slidesIn }) => ($slidesIn ? "row-reverse" : "row")};
    margin-bottom: 0;
  }
`;

const Source = styled(Copy)`
  font-family: "supply", monospace;
  color: ${({ theme, $colour }) =>
    $colour !== "white" ? theme.colours.lightGrey : theme.colours[$colour]};
  margin-right: 40px;
  margin-bottom: 12px;
  strong {
    font-family: "neue-haas-grotesk-display", sans-serif;
    display: block;
    color: ${({ theme, $colour }) =>
      $colour !== "white" ? theme.colours.lightGrey : theme.colours[$colour]};
    text-transform: uppercase;
  }

  @media (${({ theme }) => theme.mq.m}) {
    flex-direction: ${({ $slidesIn }) => $slidesIn && "row-reverse"};
    margin-bottom: 0px;
  }
`;

const getNumberSuffix = (str = "") => (str.indexOf("%") > -1 ? "%" : "");
const getNumberSeparator = (str = "") => (str.indexOf(",") > -1 ? "," : "");
const getNumberPrefix = (str = "") => (str.indexOf("$") > -1 ? "$" : "");
const sanitizeNumber = (hasDecimal, str = "") => {
  if (hasDecimal) {
    return parseFloat(str.replace(/[^\d.-]/g, ""), 10);
  }
  return parseInt(str.replace(/\D/g, ""), 10);
};

function Statistic({ stat, colour = "black", slidesIn }) {
  const prefix = getNumberPrefix(stat.number);
  const suffix = getNumberSuffix(stat.number);
  const separator = getNumberSeparator(stat.number);
  const decimals = stat.number?.replace(suffix, "")?.split(".")[1]?.length;
  const sanitizedNumber = sanitizeNumber(decimals > 0, stat?.number);
  const { ref, inView } = useInView({ triggerOnce: true });

  return (
    <Wrapper $inView={inView} $slidesIn={slidesIn}>
      <NumberWrapper $colour={colour} ref={ref} $slidesIn={slidesIn}>
        {prefersReducedMotion() ? (
          <Number $colour={colour} $slidesIn={slidesIn}>
            {stat.number}
          </Number>
        ) : (
          <CountUpNumber
            $colour={colour}
            end={inView ? sanitizedNumber : 0}
            $slidesIn={slidesIn}
            prefix={prefix}
            suffix={suffix}
            decimals={decimals}
            separator={separator}
            duration="0.5"
          />
        )}
        <H4 $colour={colour === "white" ? colour : "black"}>{stat.title}</H4>
      </NumberWrapper>
      <Sources $slidesIn={slidesIn}>
        <Source $colour={colour}>{stat.firstSource}</Source>
        {stat.secondSource && (
          <Source $colour={colour}>{stat.secondSource}</Source>
        )}
      </Sources>
    </Wrapper>
  );
}

export default Statistic;
